import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ZayacClientMessage,
  ZayacPrivateState,
  ZayacServerLobbyUpdate,
  ZayacServerMessage,
  ZayacServerPhaseChanged,
  ZayacServerSessionReplaced,
  ZayacServerError,
  ZayacVoteTotal,
  ZayacReveal,
  ZayacPlayerPublic,
} from '@shum/shared';
import { websocketUrl } from './runtime';
import { useConnectionWatchdog } from './connectionWatchdog';

export type ZayacConnectionStatus = 'connecting' | 'open' | 'closed';

export interface ZayacRoomState {
  status: ZayacConnectionStatus;
  lobby: ZayacServerLobbyUpdate | null;
  phase: ZayacServerPhaseChanged | null;
  privateState: ZayacPrivateState | null;
  voteTotals: ZayacVoteTotal[];
  reveal: ZayacReveal | null;
  scoreboard: { playerId: string; name: string; score: number }[];
  finished: { winnerPlayerId: string | null; winnerName: string | null; scoreboard: { playerId: string; name: string; score: number }[] } | null;
  error: ZayacServerError | null;
  replaced: ZayacServerSessionReplaced | null;
  hello: { roomId: string } | null;
  selectedPlayerId: string | null;
  reloginQr: { playerId: string; playerName: string; token: string; expiresAt: string } | null;
}

const INITIAL: ZayacRoomState = {
  status: 'connecting',
  lobby: null,
  phase: null,
  privateState: null,
  voteTotals: [],
  reveal: null,
  scoreboard: [],
  finished: null,
  error: null,
  replaced: null,
  hello: null,
  selectedPlayerId: null,
  reloginQr: null,
};

export function getDeviceSessionId(): string {
  const overridden = getDeviceSessionIdFromUrl();
  if (overridden) return overridden;

  try {
    const key = 'shum-device-session';
    let id = localStorage.getItem(key);
    if (!id) {
      id = `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return `dev_${Math.random().toString(36).slice(2)}`;
  }
}

function getDeviceSessionIdFromUrl(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('deviceSessionId');
    if (id && /^[A-Za-z0-9_-]{4,80}$/.test(id)) return id;
  } catch {
    // Ignore malformed URLs or non-browser test environments.
  }
  return null;
}

export function useZayacSocket(code: string | undefined) {
  const [state, setState] = useState<ZayacRoomState>(INITIAL);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number>(0);
  const closedByUs = useRef(false);
  const connectRef = useRef<(() => void) | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const lastMessageAtRef = useRef<number>(Date.now());
  const selectedPlayerIdRef = useRef<string | null>(null);
  const identityConfirmedRef = useRef(false);
  const awaitingSnapshotRef = useRef(false);
  const claimingReloginQrRef = useRef(false);

  const send = useCallback((msg: ZayacClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!code) return;
    closedByUs.current = false;
    setState((s) => ({ ...INITIAL, selectedPlayerId: s.selectedPlayerId }));

    let alive = true;

    const connect = () => {
      const current = wsRef.current;
      if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

      const url = websocketUrl(`/ws/zayac/${code}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      const reloginToken = getReloginTokenFromUrl();
      const selectedPlayerId = reloginToken ? null : selectedPlayerIdRef.current;
      claimingReloginQrRef.current = Boolean(reloginToken);
      identityConfirmedRef.current = !selectedPlayerId && !reloginToken;
      awaitingSnapshotRef.current = Boolean(selectedPlayerId || reloginToken);
      lastMessageAtRef.current = Date.now();
      setState((s) => ({ ...s, status: 'connecting' }));

      ws.onopen = () => {
        if (!alive || wsRef.current !== ws) return;
        reconnectRef.current = 0;
        lastMessageAtRef.current = Date.now();
        setState((s) => ({ ...s, status: 'connecting', error: null }));
        if (reloginToken) {
          ws.send(JSON.stringify({
            type: 'zayac.relogin_qr.claim',
            token: reloginToken,
          } satisfies ZayacClientMessage));
        } else if (selectedPlayerId) {
          ws.send(JSON.stringify({
            type: 'zayac.player.select',
            playerId: selectedPlayerId,
            deviceSessionId: getDeviceSessionId(),
          } satisfies ZayacClientMessage));
        }
      };

      ws.onmessage = (event) => {
        if (!alive || wsRef.current !== ws) return;
        lastMessageAtRef.current = Date.now();
        let msg: ZayacServerMessage;
        try {
          msg = JSON.parse(String(event.data)) as ZayacServerMessage;
        } catch {
          return;
        }
        applyMessage(msg);
      };

      ws.onclose = () => {
        if (!alive || closedByUs.current || wsRef.current !== ws) return;
        wsRef.current = null;
        setState((s) => ({ ...s, status: 'closed' }));
        reconnectRef.current += 1;
        const delay = Math.min(8000, 800 * reconnectRef.current);
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          if (alive && !closedByUs.current) connect();
        }, delay);
      };

      ws.onerror = () => {
        /* onclose will follow */
      };
    };

    const applyMessage = (msg: ZayacServerMessage) => {
      setState((prev) => {
        switch (msg.type) {
          case 'hello':
            return {
              ...prev,
              hello: { roomId: msg.roomId },
              status: awaitingSnapshotRef.current ? 'connecting' : 'open',
            };
          case 'zayac.lobby.update': {
            const synchronized = !awaitingSnapshotRef.current || identityConfirmedRef.current;
            if (synchronized) awaitingSnapshotRef.current = false;
            return { ...prev, lobby: msg, status: synchronized ? 'open' : prev.status };
          }
          case 'zayac.phase.changed':
            return { ...prev, phase: msg };
          case 'zayac.phase.timer':
            return prev;
          case 'zayac.private_state': {
            const synchronized = !awaitingSnapshotRef.current || identityConfirmedRef.current;
            if (synchronized) awaitingSnapshotRef.current = false;
            return {
              ...prev,
              status: synchronized ? 'open' : prev.status,
              phase: {
                type: 'zayac.phase.changed',
                phase: msg.state.phase,
                roundNumber: msg.state.roundNumber,
                endsAt: msg.state.endsAt,
              },
              privateState: msg.state,
            };
          }
          case 'zayac.vote.totals':
            return { ...prev, voteTotals: msg.totals };
          case 'zayac.round.revealed':
            return { ...prev, reveal: msg.reveal };
          case 'zayac.scoreboard':
            return { ...prev, scoreboard: msg.scoreboard };
          case 'zayac.game.finished':
            return { ...prev, finished: msg };
          case 'zayac.relogin_qr':
            return {
              ...prev,
              reloginQr: {
                playerId: msg.playerId,
                playerName: msg.playerName,
                token: msg.token,
                expiresAt: msg.expiresAt,
              },
            };
          case 'session_replaced':
            selectedPlayerIdRef.current = null;
            identityConfirmedRef.current = true;
            awaitingSnapshotRef.current = false;
            return { ...prev, status: 'open', replaced: msg, selectedPlayerId: null, privateState: null, reloginQr: null };
          case 'reconnect':
            selectedPlayerIdRef.current = msg.playerId;
            identityConfirmedRef.current = true;
            if (claimingReloginQrRef.current) {
              claimingReloginQrRef.current = false;
              clearReloginTokenFromUrl();
            }
            return { ...prev, selectedPlayerId: msg.playerId, replaced: null, error: null };
          case 'error':
            if (claimingReloginQrRef.current) {
              claimingReloginQrRef.current = false;
              clearReloginTokenFromUrl();
              selectedPlayerIdRef.current = null;
              identityConfirmedRef.current = true;
              awaitingSnapshotRef.current = false;
              return {
                ...prev,
                status: 'open',
                selectedPlayerId: null,
                privateState: null,
                error: msg,
              };
            }
            if (awaitingSnapshotRef.current && msg.code === 'PLAYER_NOT_FOUND') {
              selectedPlayerIdRef.current = null;
              identityConfirmedRef.current = true;
              awaitingSnapshotRef.current = false;
              return {
                ...prev,
                status: 'open',
                selectedPlayerId: null,
                privateState: null,
                error: msg,
              };
            }
            return { ...prev, error: msg };
          case 'pong':
            return prev;
          default:
            return prev;
        }
      });
    };

    connectRef.current = connect;
    const connectTimer = window.setTimeout(connect, 0);

    return () => {
      alive = false;
      closedByUs.current = true;
      connectRef.current = null;
      window.clearTimeout(connectTimer);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [code]);

  // Force a fresh connection with the same parameters (manual button / watchdog).
  const reconnect = useCallback(() => {
    if (closedByUs.current || !connectRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    const ws = wsRef.current;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        // Already closing/closed; a new socket replaces it below.
      }
    }
    wsRef.current = null;
    reconnectRef.current = 0;
    identityConfirmedRef.current = !selectedPlayerIdRef.current;
    awaitingSnapshotRef.current = Boolean(selectedPlayerIdRef.current);
    setState((s) => ({ ...s, status: 'connecting' }));
    connectRef.current();
  }, []);

  useConnectionWatchdog({ wsRef, stoppedRef: closedByUs, lastMessageAtRef, reconnect });

  const selectPlayer = useCallback(
    (playerId: string) => {
      const ok = send({ type: 'zayac.player.select', playerId, deviceSessionId: getDeviceSessionId() });
      if (ok) {
        selectedPlayerIdRef.current = playerId;
        identityConfirmedRef.current = false;
        awaitingSnapshotRef.current = true;
        setState((s) => ({ ...s, status: 'connecting', selectedPlayerId: playerId }));
      }
      return ok;
    },
    [send],
  );

  const createReloginQr = useCallback(() => {
    const ok = send({ type: 'zayac.relogin_qr.create' });
    if (ok) setState((s) => ({ ...s, reloginQr: null, error: null }));
    return ok;
  }, [send]);

  const clearReloginQr = useCallback(() => setState((s) => ({ ...s, reloginQr: null })), []);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null })), []);

  return useMemo(
    () => ({ state, send, selectPlayer, createReloginQr, clearReloginQr, clearError, reconnect }),
    [state, send, selectPlayer, createReloginQr, clearReloginQr, clearError, reconnect],
  );
}

function getReloginTokenFromUrl(): string | null {
  try {
    const token = new URLSearchParams(window.location.search).get('relogin');
    return token && /^[a-f0-9]{48}$/.test(token) ? token : null;
  } catch {
    return null;
  }
}

function clearReloginTokenFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('relogin')) return;
    url.searchParams.delete('relogin');
    window.history.replaceState(null, '', url);
  } catch {
    // The authenticated socket remains valid if History API is unavailable.
  }
}

export type ZayacLobbyPlayers = ZayacPlayerPublic[];
